/**
 * Shared tooth rendering utilities for SVG tooth charts.
 * Extracted from PlaqueAndBleedingChart for reuse across comparison charts.
 */

// ─── Catmull-Rom spline → smooth closed SVG path ───
export const pointsToSmoothPath = (pts) => {
    const n = pts.length;
    if (n < 3) return "";

    const g = (i) => pts[((i % n) + n) % n];
    let d = `M ${g(0)[0].toFixed(1)} ${g(0)[1].toFixed(1)}`;

    for (let i = 0; i < n; i++) {
        const [x0, y0] = g(i - 1);
        const [x1, y1] = g(i);
        const [x2, y2] = g(i + 1);
        const [x3, y3] = g(i + 2);
        d +=
            ` C ${(x1 + (x2 - x0) / 6).toFixed(1)} ${(y1 + (y2 - y0) / 6).toFixed(1)},` +
            `${(x2 - (x3 - x1) / 6).toFixed(1)} ${(y2 - (y3 - y1) / 6).toFixed(1)},` +
            `${x2.toFixed(1)} ${y2.toFixed(1)}`;
    }

    return d + " Z";
};

// ─── Anatomical occlusal outlines (normalized ±1) ───
// Upper teeth: buccal at top (-y), palatal at bottom (+y)
// Lower teeth: lingual at top (-y), buccal at bottom (+y)
// Points clockwise from upper-left area
export const O = {
    uM: [
        [-0.85, -0.92],
        [-0.3, -1.0],
        [0.15, -0.88],
        [0.45, -0.97],
        [0.82, -0.88],
        [0.98, -0.35],
        [0.92, 0.25],
        [0.62, 0.82],
        [0.15, 0.92],
        [-0.45, 1.0],
        [-0.88, 0.62],
        [-1.0, 0.0],
        [-0.95, -0.48]
    ],
    uM8: [
        [-0.78, -0.82],
        [-0.1, -1.0],
        [0.65, -0.88],
        [0.95, -0.22],
        [0.88, 0.38],
        [0.48, 0.82],
        [-0.08, 0.95],
        [-0.68, 0.78],
        [-0.92, 0.18],
        [-0.88, -0.38]
    ],
    uP: [
        [-0.62, -0.88],
        [0.0, -1.0],
        [0.62, -0.88],
        [0.92, -0.22],
        [0.82, 0.38],
        [0.32, 0.88],
        [0.0, 1.0],
        [-0.32, 0.88],
        [-0.82, 0.38],
        [-0.92, -0.22]
    ],
    uC: [
        [-0.28, -0.88],
        [0.0, -1.0],
        [0.28, -0.88],
        [0.68, -0.38],
        [0.85, 0.08],
        [0.65, 0.52],
        [0.28, 0.88],
        [0.0, 0.95],
        [-0.28, 0.88],
        [-0.65, 0.52],
        [-0.85, 0.08],
        [-0.68, -0.38]
    ],
    uI1: [
        [-0.88, -0.72],
        [-0.42, -0.95],
        [0.0, -1.0],
        [0.42, -0.95],
        [0.88, -0.72],
        [0.82, 0.0],
        [0.58, 0.48],
        [0.22, 0.85],
        [0.0, 0.95],
        [-0.22, 0.85],
        [-0.58, 0.48],
        [-0.82, 0.0]
    ],
    uI2: [
        [-0.72, -0.72],
        [-0.18, -0.95],
        [0.18, -0.95],
        [0.72, -0.72],
        [0.85, -0.12],
        [0.65, 0.42],
        [0.22, 0.85],
        [0.0, 0.95],
        [-0.22, 0.85],
        [-0.65, 0.42],
        [-0.85, -0.12]
    ],
    lM: [
        [-0.88, -0.82],
        [-0.32, -0.95],
        [0.28, -0.95],
        [0.85, -0.82],
        [1.0, -0.18],
        [0.95, 0.22],
        [0.68, 0.82],
        [0.28, 0.95],
        [-0.12, 0.85],
        [-0.52, 0.95],
        [-0.88, 0.78],
        [-1.0, 0.08],
        [-0.95, -0.38]
    ],
    lM8: [
        [-0.78, -0.78],
        [-0.1, -0.95],
        [0.58, -0.85],
        [0.88, -0.22],
        [0.85, 0.28],
        [0.52, 0.78],
        [-0.08, 0.95],
        [-0.58, 0.82],
        [-0.88, 0.22],
        [-0.85, -0.32]
    ],
    lP: [
        [-0.58, -0.78],
        [0.0, -0.88],
        [0.58, -0.78],
        [0.85, -0.12],
        [0.68, 0.48],
        [0.32, 0.85],
        [0.0, 1.0],
        [-0.32, 0.85],
        [-0.68, 0.48],
        [-0.85, -0.12]
    ],
    lC: [
        [-0.28, -0.82],
        [0.0, -0.92],
        [0.28, -0.82],
        [0.65, -0.38],
        [0.82, 0.08],
        [0.58, 0.52],
        [0.22, 0.88],
        [0.0, 1.0],
        [-0.22, 0.88],
        [-0.58, 0.52],
        [-0.82, 0.08],
        [-0.65, -0.38]
    ],
    lI1: [
        [-0.62, -0.68],
        [0.0, -0.82],
        [0.62, -0.68],
        [0.82, 0.0],
        [0.58, 0.48],
        [0.22, 0.85],
        [0.0, 0.95],
        [-0.22, 0.85],
        [-0.58, 0.48],
        [-0.82, 0.0]
    ],
    lI2: [
        [-0.68, -0.68],
        [-0.15, -0.85],
        [0.15, -0.85],
        [0.68, -0.68],
        [0.82, 0.0],
        [0.58, 0.48],
        [0.15, 0.88],
        [0.0, 0.95],
        [-0.15, 0.88],
        [-0.58, 0.48],
        [-0.82, 0.0]
    ]
};

export const getOutlineKey = (toothNum) => {
    const q = Math.floor(toothNum / 10);
    const d = toothNum % 10;
    const u = q <= 2 || (q >= 5 && q <= 6);
    if (d >= 6) return d === 8 ? (u ? "uM8" : "lM8") : u ? "uM" : "lM";
    if (d >= 4) return u ? "uP" : "lP";
    if (d === 3) return u ? "uC" : "lC";
    if (d === 2) return u ? "uI2" : "lI2";
    return u ? "uI1" : "lI1";
};

// ─── 6 wedge sections: X pattern + horizontal center line ───
export const SECTIONS = ["top", "upperLeft", "upperRight", "bottom", "lowerLeft", "lowerRight"];

export const getSectionPolygon = (rx, ry) => {
    const e = 1.5;
    return {
        top: `0,0 ${-rx * e},${-ry * e} ${rx * e},${-ry * e}`,
        upperLeft: `0,0 ${-rx * e},${-ry * e} ${-rx * e},0`,
        upperRight: `0,0 ${rx * e},${-ry * e} ${rx * e},0`,
        bottom: `0,0 ${rx * e},${ry * e} ${-rx * e},${ry * e}`,
        lowerLeft: `0,0 ${-rx * e},${ry * e} ${-rx * e},0`,
        lowerRight: `0,0 ${rx * e},0 ${rx * e},${ry * e}`
    };
};

export const getSiteKey = (toothNum, section) => {
    const quadrant = Math.floor(toothNum / 10);
    const isUpper = quadrant <= 2 || (quadrant >= 5 && quadrant <= 6);
    const isRight = quadrant === 1 || quadrant === 4 || quadrant === 5 || quadrant === 8;

    const topSurf = isUpper ? "b" : "l";
    const botSurf = isUpper ? "p" : "b";
    const lPfx = isRight ? "d" : "m";
    const rPfx = isRight ? "m" : "d";

    switch (section) {
        case "top":
            return topSurf;
        case "upperLeft":
            return lPfx + topSurf;
        case "upperRight":
            return rPfx + topSurf;
        case "bottom":
            return botSurf;
        case "lowerLeft":
            return lPfx + botSurf;
        case "lowerRight":
            return rPfx + botSurf;
        default:
            return topSurf;
    }
};

export const getToothSize = (toothNum) => {
    const d = toothNum % 10;
    const isDeciduous = Math.floor(toothNum / 10) >= 5;
    const s = isDeciduous ? 0.85 : 1;

    switch (d) {
        case 8:
            return { rx: 14 * s, ry: 13 * s };
        case 7:
            return { rx: 17 * s, ry: 15 * s };
        case 6:
            return { rx: 18 * s, ry: 16 * s };
        case 5:
            return { rx: 15 * s, ry: 15 * s };
        case 4:
            return { rx: 15 * s, ry: 15 * s };
        case 3:
            return { rx: 14 * s, ry: 16 * s };
        case 2:
            return { rx: 15 * s, ry: 15 * s };
        case 1:
            return { rx: 14 * s, ry: 15 * s };
        default:
            return { rx: 12 * s, ry: 14 * s };
    }
};

export const getOutlinePath = (toothNum) => {
    const { rx, ry } = getToothSize(toothNum);
    const pts = O[getOutlineKey(toothNum)];
    return pointsToSmoothPath(pts.map(([x, y]) => [x * rx, y * ry]));
};

// ─── Layout constants (shared across all tooth charts) ───
export const LAYOUT = {
    cellWidth: 42,
    midlineGap: 6,
    maxRy: 16,
    get rowHeight() {
        return this.maxRy * 2 + 4;
    },
    get svgWidth() {
        return 16 * this.cellWidth + this.midlineGap;
    },
    getToothX(idx) {
        return idx * this.cellWidth + this.cellWidth / 2 + (idx >= 8 ? this.midlineGap : 0);
    }
};
